import React, { useState, useMemo, useCallback } from 'react';
import {
  RefreshCw, ChevronRight, ChevronDown, ChevronLeft, AlertTriangle, Eye, Search, X, Truck, Plus,
  Minus, Send, Save, Trash2, Clock, CheckCircle2, Info, PackageX, UserCheck, ClipboardCheck, CalendarRange,
} from 'lucide-react';
import { plannerAPI, medicineOrderAPI, orderReviewAPI } from '../../services/api';
import { useToast, useConfirm } from '../UIProvider';
import Select from '../Select';
import RowActionsMenu from '../RowActionsMenu';
import useDialog from '../../hooks/useDialog';
import { formatClinicDate, clinicToday } from '../../utils/clinicTime';
import { printOrderSheet } from './orderSheet';
import ShareForReviewDialog from './ShareForReviewDialog';
import { packKind, unitsIn } from './packWords';

// The Order Board, in two steps:
//
//   1. One card per supplier: when to order, when it would arrive, how many items and how urgent.
//      Anything that runs out before any delivery can arrive is called out across the top.
//   2. Open a supplier: a plain list with a tick, a packs stepper and the price per line, "Why?"
//      for the sentence behind the suggestion, and Save draft, Send for review or Send at the bottom.
//
// The planner proposes and explains; a person decides. Nothing is ordered until Send. An order sent
// for review waits with the colleague who checks it, and is listed here under "Out for review".
// A supplier has one order at a time: while theirs is out for review, whatever else the board has
// for them (or is moved to them) is added to it, never sent as a second order.
//
// Above it all, how long the orders are to last: the planner's own choice per item, or a period
// chosen for everything at once (15 days, 45 days...), when every item that will not last it after
// its delivery is ticked with enough to last it.

/** The periods offered at a click; any other from 7 to 120 days can be typed. */
export const PERIODS = [15, 30, 45, 60, 90];
const MIN_PERIOD = 7;
const MAX_PERIOD = 120;

export const TIER = {
  ORDER_TODAY: { label: 'Runs out before any delivery', short: 'Urgent', chip: 'bg-red-100 text-red-800 border-red-200', bar: 'border-l-red-500', dot: 'bg-red-500' },
  THIS_ORDER: { label: 'Needed in this order', short: 'This order', chip: 'bg-amber-100 text-amber-800 border-amber-200', bar: 'border-l-amber-400', dot: 'bg-amber-400' },
  NEXT_ORDER: { label: 'Can wait for the next order', short: 'Can wait', chip: 'bg-gray-100 text-gray-700 border-gray-200', bar: 'border-l-gray-300', dot: 'bg-gray-300' },
  FINE: { label: 'Added by hand', short: 'Added', chip: 'bg-navy-50 text-navy-700 border-navy-100', bar: 'border-l-navy-300', dot: 'bg-navy-300' },
};

export const CONFIDENCE = {
  MEASURED: { label: 'from sales', title: "Worked out from this system's own sales" },
  BLENDED: { label: 'partly from sales', title: "This system's sales so far, blended with the old system's counts" },
  SEEDED: { label: 'from old records', title: "From the old system's counts, until this one has a week of sales" },
  UNKNOWN: { label: 'no history', title: 'No usage known yet: the quantity is a placeholder' },
};

const SKIP_REASONS = [
  { id: 'NOT_NEEDED', label: 'Not needed now', hint: 'Left off until the order after this one.' },
  { id: 'ELSEWHERE', label: 'Bought elsewhere', hint: 'Left off until the order after this one.' },
  { id: 'OVERSTOCKED', label: 'Too much in stock', hint: 'Its cover is cut by a week, and it is left off this time.' },
  { id: 'STOPPED', label: 'No longer ordering it', hint: 'Left off for a year. Put it back in Item Settings.' },
];

// Where the migration filed opening stock: not a supplier anybody can order from.
export const isOpeningStock = (v) => (v?.vendorName || '').trim().toLowerCase() === 'legacy opening stock';

export const rs = (n) => (n == null ? '' : `Rs ${Math.round(Number(n)).toLocaleString()}`);
const day = (d) => (d ? formatClinicDate(d, { weekday: 'short', day: 'numeric', month: 'short' }) : '');

const addDays = (iso, n) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
};

/** "Order today", "Order tomorrow" or "Order by Thu 9 Oct". */
const whenToOrder = (nextOrder) => {
  if (!nextOrder) return '';
  const today = clinicToday();
  if (nextOrder <= today) return 'Order today';
  if (nextOrder === addDays(today, 1)) return 'Order tomorrow';
  return `Order by ${day(nextOrder)}`;
};

/** Where a supplier's order out for review is: with the reviewer, or back for its raiser to accept. */
const reviewWhere = (r) => (r.status === 'REVIEWED'
  ? `Back from review, for ${r.raisedByName || 'the person who raised it'} to accept`
  : `Out for review with ${r.reviewerName || 'a colleague'}`);

/** Opens one order on the Order Reviews page, which reads the order from the address. */
export const openOrderReview = (orderId) => { window.location.hash = `/order-reviews/${orderId}`; };

const OrderBoardView = ({
  board, loading, error, onReload, planDays = null, onPlanDaysChange, vendors, medicines, can, branding, onChanged,
}) => {
  const notify = useToast();
  const confirm = useConfirm();
  const [openVendor, setOpenVendor] = useState(null); // vendorId, 'none', or null for the cards
  const [kind, setKind] = useState('all');
  const [search, setSearch] = useState('');
  // What a person has changed, per item: { include, packs, vendorId }. Kept across reloads so
  // leaving one line off does not undo the packs typed on the others.
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(false);
  const [skipFor, setSkipFor] = useState(null);
  const [moveFor, setMoveFor] = useState(null);
  const [showWatch, setShowWatch] = useState(false);
  const [shareFor, setShareFor] = useState(null);

  const vendorById = useMemo(() => new Map((vendors || []).map(v => [v.id, v])), [vendors]);
  // Each supplier's order out for review (the oldest, should there be two): their one order, which
  // anything more for them joins.
  const reviewByVendor = useMemo(() => {
    const byVendor = new Map();
    [...(board?.reviews || [])]
      .sort((a, b) => String(a.orderNumber).localeCompare(String(b.orderNumber)))
      .forEach(r => { if (r.vendorId && !byVendor.has(r.vendorId)) byVendor.set(r.vendorId, r); });
    return byVendor;
  }, [board]);
  const sectionById = useMemo(() => new Map((board?.vendors || []).map(s => [s.vendorId, s])), [board]);

  // Every line, flat, with the person's changes applied. A line moved to another supplier takes
  // that supplier's last price, from the alternatives the planner listed.
  const lines = useMemo(() => {
    if (!board) return [];
    const all = [...board.vendors.flatMap(s => s.lines), ...board.unassigned];
    return all.map(l => {
      const e = edits[l.medicineId] || {};
      const vendorId = e.vendorId !== undefined ? e.vendorId : l.vendorId;
      const alt = vendorId !== l.vendorId ? (l.alternatives || []).find(a => a.vendorId === vendorId) : null;
      const packPrice = vendorId === l.vendorId ? l.packPrice : alt ? alt.packPrice : null;
      const packs = e.packs !== undefined ? e.packs : l.packs;
      const include = e.include !== undefined ? e.include : l.include;
      return { ...l, vendorId, packPrice, packs, include, moved: vendorId !== l.vendorId,
        lineCost: packPrice != null && packs ? Number(packPrice) * Number(packs) : null };
    });
  }, [board, edits]);

  const matches = useCallback((l) => {
    const q = search.trim().toLowerCase();
    return (kind === 'all' || (kind === 'supply' ? l.category === 'SUPPLY' : l.category !== 'SUPPLY'))
      && (!q || `${l.name} ${l.code || ''} ${l.vendorItemName || ''}`.toLowerCase().includes(q));
  }, [kind, search]);

  // One card per supplier holding a line, urgent ones first, then by the day to order.
  const cards = useMemo(() => {
    const groups = new Map();
    lines.forEach(l => {
      const key = l.vendorId || 'none';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(l);
    });
    const out = [];
    groups.forEach((ls, key) => {
      const meta = key === 'none'
        ? { vendorId: null, vendorName: 'No supplier yet' }
        : sectionById.get(key) || { vendorId: key, vendorName: vendorById.get(key)?.vendorName || 'Supplier' };
      const shown = ls.filter(matches);
      if (shown.length === 0) return;
      const ticked = ls.filter(l => l.include);
      out.push({
        ...meta,
        key,
        lines: ls,
        shown,
        urgent: ls.filter(l => l.tier === 'ORDER_TODAY').length,
        thisOrder: ls.filter(l => l.tier === 'THIS_ORDER').length,
        canWait: ls.filter(l => l.tier === 'NEXT_ORDER').length,
        ticked: ticked.length,
        total: ticked.reduce((s, l) => s + (l.lineCost || 0), 0),
        consolidate: ls.find(l => l.consolidateWith)?.consolidateWith,
        // A draft of theirs is their order in the making; failing one, the order out for review.
        review: key !== 'none' && !meta.draftOrderNumber ? reviewByVendor.get(key) || null : null,
      });
    });
    out.sort((a, b) => (a.key === 'none') - (b.key === 'none')
      || (b.urgent > 0) - (a.urgent > 0)
      || String(a.nextOrder || '').localeCompare(String(b.nextOrder || ''))
      || a.vendorName.localeCompare(b.vendorName));
    return out;
  }, [lines, sectionById, vendorById, matches, reviewByVendor]);

  const urgentLines = lines.filter(l => l.tier === 'ORDER_TODAY' && l.inDraftPacks == null);

  const setEdit = (id, patch) => setEdits(prev => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));
  const forget = (ids) => setEdits(prev => {
    const next = { ...prev };
    ids.forEach(id => delete next[id]);
    return next;
  });

  const reload = async () => { await onReload(); onChanged && onChanged(); };

  // A new period brings new suggestions for every line, so changes not yet saved give way to them.
  const choosePeriod = async (days) => {
    if ((days || null) === (planDays || null)) return;
    if (Object.keys(edits).length > 0) {
      const ok = await confirm({
        title: days ? `Plan for ${days} days?` : "Go back to the planner's choice?",
        message: 'The ticks and packs changed on the board and not yet saved are replaced by the new suggestions. Saved drafts stay as they are.',
        confirmText: 'Change',
      });
      if (!ok) return;
    }
    setEdits({});
    onPlanDaysChange && onPlanDaysChange(days || null);
  };

  // ------------------------------------------------------------------ supplier actions

  const tickedLinesOf = (card) => card.lines.filter(l => l.include && Number(l.packs) >= 1)
    .map(l => ({ medicineId: l.medicineId, packs: Number(l.packs) }));

  const saveDraft = async (card, extra = []) => {
    const body = [...tickedLinesOf(card), ...extra];
    const bad = card.lines.find(l => l.include && !(Number(l.packs) >= 1));
    if (bad) { notify('warning', `Set at least one pack for ${bad.name}, or untick it.`); return null; }
    setBusy(true);
    try {
      const res = await plannerAPI.replaceDraft(card.vendorId, body, board.periodDays ?? null);
      forget(card.lines.map(l => l.medicineId));
      await reload();
      return res;
    } catch (e) {
      notify('error', e.message || 'Could not save the draft');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const onSave = async (card) => {
    const res = await saveDraft(card);
    if (res && !res.discarded) notify('success', `Draft ${res.orderNumber} saved for ${card.vendorName}`);
  };

  const onSend = async (card) => {
    const n = tickedLinesOf(card).length;
    const ok = await confirm({
      title: `Send the order to ${card.vendorName}?`,
      message: `${n} item${n === 1 ? '' : 's'}, about ${rs(card.total)}. The order sheet opens to print, and the items count as on order from now on.`,
      confirmText: 'Send order',
    });
    if (!ok) return;
    const draft = await saveDraft(card);
    if (!draft || draft.discarded) return;
    try {
      const sent = await medicineOrderAPI.submit(draft.id);
      if (!printOrderSheet(sent, branding)) notify('warning', 'The order was sent, but the browser blocked the print window. Print it from Orders.');
      notify('success', `${sent.orderNumber} sent to ${card.vendorName}`);
      setOpenVendor(null);
      await reload();
    } catch (e) {
      notify('error', e.message || 'Could not send the order');
      await reload();
    }
  };

  // Saves the supplier's list as a draft, then hands the draft to the colleague chosen. It leaves
  // the board until it comes back.
  const onShare = async (card, reviewerId, note, person) => {
    const draft = await saveDraft(card);
    if (!draft || draft.discarded) return;
    try {
      await orderReviewAPI.share(draft.id, reviewerId, note);
      setShareFor(null);
      setOpenVendor(null);
      notify('success', `${draft.orderNumber} shared with ${person?.fullName || 'your colleague'} for review`);
      window.dispatchEvent(new Event('clinic:order-reviews-changed'));
      await reload();
    } catch (e) {
      notify('error', e.message || 'Could not share the order');
      await reload();
    }
  };

  const onDiscard = async (card) => {
    const ok = await confirm({ title: `Discard the draft for ${card.vendorName}?`, message: 'The suggestions stay on the board; only the saved draft goes.', confirmText: 'Discard', tone: 'danger' });
    if (!ok) return;
    setBusy(true);
    try {
      await plannerAPI.replaceDraft(card.vendorId, []);
      forget(card.lines.map(l => l.medicineId));
      await reload();
      notify('success', 'Draft discarded');
    } catch (e) { notify('error', e.message || 'Could not discard the draft'); }
    finally { setBusy(false); }
  };

  // A supplier whose order is out for review gets these added to it, not a second order.
  const addToReview = async (card, body) => {
    setBusy(true);
    try {
      const res = await plannerAPI.addToOrderInReview(card.review.orderId, body, board.periodDays ?? null);
      forget(body.map(l => l.medicineId));
      const names = [...(res.added || []), ...(res.raised || [])];
      if (names.length) {
        notify('success', `${names.length === 1 ? names[0] : `${names.length} items`} added to ${res.orderNumber} for ${card.vendorName}${res.orderStatus === 'IN_REVIEW' ? ', for the reviewer to check with the rest' : ''}`);
      }
      if (res.alreadyThere?.length) {
        notify('info', `${res.alreadyThere.join(', ')} ${res.alreadyThere.length === 1 ? 'is' : 'are'} already on ${res.orderNumber}: left as it is there.`);
      }
      window.dispatchEvent(new Event('clinic:order-reviews-changed'));
      setOpenVendor(null);
      await reload();
    } catch (e) {
      notify('error', e.message || 'Could not add them to the order');
    } finally {
      setBusy(false);
    }
  };

  const onAddToReview = (card) => {
    const bad = card.lines.find(l => l.include && !(Number(l.packs) >= 1));
    if (bad) { notify('warning', `Set at least one pack for ${bad.name}, or untick it.`); return; }
    addToReview(card, tickedLinesOf(card));
  };

  const onAddItem = async (card, medicineId) => {
    if (!medicineId) return;
    if (card.lines.some(l => l.medicineId === medicineId)) { notify('info', 'That item is already on this list.'); return; }
    if (card.review) { await addToReview(card, [{ medicineId, packs: null }]); return; }
    const res = await saveDraft(card, [{ medicineId, packs: null }]);
    if (res && !res.discarded) notify('success', 'Added. Check the packs.');
  };

  const onSkip = async (line, reason) => {
    setSkipFor(null);
    try {
      const res = await plannerAPI.skip(line.medicineId, line.vendorId, reason);
      forget([line.medicineId]);
      notify('success', `${line.name}: ${res.message}`);
      await reload();
    } catch (e) { notify('error', e.message || 'Could not leave it off'); }
  };

  // ------------------------------------------------------------------ render

  if (loading && !board) {
    return <div className="flex justify-center py-20"><div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div></div>;
  }
  if (error) {
    return <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg text-red-700">{error}</div>;
  }
  if (!board) return null;

  const card = openVendor ? cards.find(c => c.key === openVendor) : null;
  if (openVendor && card) {
    return (
      <SupplierPanel
        card={card}
        board={board}
        periodDays={board.periodDays ?? null}
        can={can}
        busy={busy}
        medicines={medicines}
        onBack={() => setOpenVendor(null)}
        onEdit={setEdit}
        onSave={() => onSave(card)}
        onSend={() => onSend(card)}
        onShare={() => setShareFor(card)}
        onAddToReview={() => onAddToReview(card)}
        onDiscard={() => onDiscard(card)}
        onAddItem={(id) => onAddItem(card, id)}
        onSkip={setSkipFor}
        onMove={setMoveFor}
        skipFor={skipFor}
        moveFor={moveFor}
        closeSkip={() => setSkipFor(null)}
        closeMove={() => setMoveFor(null)}
        doSkip={onSkip}
        doMove={(line, vendorId) => { setMoveFor(null); setEdit(line.medicineId, { vendorId, include: true }); notify('info', `${line.name} moved to ${vendorById.get(vendorId)?.vendorName || 'the other supplier'}.`); }}
        vendors={vendors}
        shareDialog={shareFor && shareFor.key === card.key && (
          <ShareForReviewDialog
            title={`Send ${card.vendorName}'s order for review`}
            subtitle={`${tickedLinesOf(card).length} item${tickedLinesOf(card).length === 1 ? '' : 's'}, about ${rs(card.total)}`}
            onClose={() => setShareFor(null)}
            onShare={(reviewerId, note, person) => onShare(card, reviewerId, note, person)}
          />
        )}
      />
    );
  }

  const watch = board.watch || {};
  const watchGroups = [
    ['chase', 'On order but running out', 'text-red-700'],
    ['priceRises', 'Price went up on the last bill', 'text-amber-800'],
    ['expiring', 'Will expire before it is used', 'text-amber-800'],
    ['overstocked', 'More than 90 days on the shelf', 'text-navy-800'],
    ['skipped', 'Left off for now', 'text-gray-700'],
    ['leftOff', 'Left off orders', 'text-gray-700'],
  ].filter(([k]) => (watch[k] || []).length > 0);
  const watchCount = watchGroups.reduce((n, [k]) => n + watch[k].length, 0);

  return (
    <div className="space-y-4">
      <PeriodPicker planDays={planDays} board={board} loading={loading} onChoose={choosePeriod} notify={notify} />

      {urgentLines.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
          <p className="font-semibold text-red-800 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            {urgentLines.length} item{urgentLines.length === 1 ? '' : 's'} will run out before any delivery can arrive
          </p>
          <p className="text-sm text-red-700 mt-1">Order them today, or buy locally to bridge the gap.</p>
          <div className="flex flex-wrap gap-2 mt-3">
            {urgentLines.slice(0, 12).map(l => (
              <button key={l.medicineId} type="button" onClick={() => setOpenVendor(l.vendorId || 'none')}
                className="px-3 py-1 rounded-full bg-white border border-red-200 text-sm text-red-800 hover:bg-red-100">
                {l.name}{l.runOut ? ` · out ${day(l.runOut)}` : ''}
              </button>
            ))}
            {urgentLines.length > 12 && <span className="text-sm text-red-700 self-center">and {urgentLines.length - 12} more</span>}
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[14rem]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Find an item"
            className="w-full pl-9 pr-9 py-2.5 bg-white border border-gray-200 rounded-xl text-sm" />
          {search && (
            <button type="button" aria-label="Clear search" onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-100">
              <X className="h-4 w-4 text-gray-400" />
            </button>
          )}
        </div>
        <div className="flex gap-1 bg-white rounded-xl p-1 border border-gray-200">
          {[['all', 'Everything'], ['medicine', 'Medicines'], ['supply', 'Supplies']].map(([id, label]) => (
            <button key={id} type="button" aria-pressed={kind === id} onClick={() => setKind(id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${kind === id ? 'bg-emerald-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}>
              {label}
            </button>
          ))}
        </div>
        <button type="button" onClick={onReload} disabled={loading} className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-white border border-gray-200 text-sm text-gray-700 hover:bg-gray-50">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <ReviewCards reviews={board.reviews || []} />

      {cards.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
          <CheckCircle2 className="h-14 w-14 text-emerald-300 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-gray-700">{search ? 'Nothing matches' : 'Nothing needs ordering'}</h3>
          <p className="text-gray-500 text-sm">{search ? 'Try another name.' : board.periodDays ? `Every item lasts ${board.periodDays} days after its delivery.` : "Every item lasts until after its supplier's next two deliveries."}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {cards.map(c => (
            <button key={c.key} type="button" onClick={() => setOpenVendor(c.key)}
              className={`text-left bg-white rounded-2xl shadow-sm border hover:shadow-md transition-shadow p-5 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${c.urgent > 0 ? 'border-red-200' : 'border-gray-100'}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-bold text-gray-900 truncate">{c.vendorName}</h3>
                  {c.vendorId && c.review ? (
                    <>
                      <p className="text-sm font-semibold mt-0.5 text-violet-800">Joins {c.review.orderNumber}</p>
                      <p className="text-xs text-gray-500">{reviewWhere(c.review)}</p>
                    </>
                  ) : c.vendorId ? (
                    <>
                      {c.nextOrder && <p className={`text-sm font-semibold mt-0.5 ${c.urgent > 0 ? 'text-red-700' : 'text-navy-800'}`}>{whenToOrder(c.nextOrder)}</p>}
                      {c.nextDelivery && <p className="text-xs text-gray-500">Arrives about {day(c.nextDelivery)}</p>}
                    </>
                  ) : (
                    <p className="text-sm text-gray-600 mt-0.5">Choose a supplier for each</p>
                  )}
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-4 text-sm">
                {c.urgent > 0 && <span className="flex items-center gap-1.5 text-red-800"><span className="w-2 h-2 rounded-full bg-red-500" />{c.urgent} urgent</span>}
                {c.thisOrder > 0 && <span className="flex items-center gap-1.5 text-amber-800"><span className="w-2 h-2 rounded-full bg-amber-400" />{c.thisOrder} needed</span>}
                {c.canWait > 0 && <span className="flex items-center gap-1.5 text-gray-600"><span className="w-2 h-2 rounded-full bg-gray-300" />{c.canWait} can wait</span>}
              </div>
              {search && c.shown.length < c.lines.length && (
                <p className="text-xs text-gray-500 mt-2 truncate">Has {c.shown.map(l => l.name).join(', ')}</p>
              )}
              <div className="flex items-end justify-between mt-4 pt-3 border-t border-gray-100">
                <div className="text-xs text-gray-500 space-y-0.5">
                  <p>{c.ticked} item{c.ticked === 1 ? '' : 's'} ticked</p>
                  {c.draftOrderNumber && <p className="text-navy-700 font-medium">Draft saved</p>}
                  {c.unreliable && <p className="text-amber-700">Often delivers short</p>}
                  {c.consolidate && <p className="text-navy-700">Could go with {c.consolidate}</p>}
                </div>
                {c.total > 0 || c.ticked === 0
                  ? <p className="text-lg font-bold text-gray-900">{rs(c.total)}</p>
                  : <p className="text-sm text-gray-500">No price yet</p>}
              </div>
            </button>
          ))}
        </div>
      )}

      {watchCount > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
          <button type="button" onClick={() => setShowWatch(v => !v)} aria-expanded={showWatch}
            className="w-full flex items-center justify-between p-4 text-left rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500">
            <span className="font-semibold text-gray-800 flex items-center gap-2"><Eye className="w-5 h-5 text-navy-700" /> Worth a look ({watchCount})</span>
            {showWatch ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
          </button>
          {showWatch && (
            <div className="px-4 pb-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              {watchGroups.map(([key, title, cls]) => (
                <div key={key}>
                  <p className={`font-semibold mb-1 ${cls}`}>{title}</p>
                  <ul className="space-y-1">
                    {watch[key].map(w => <li key={w.medicineId} className="text-gray-700"><b>{w.name}</b>: {w.what}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// =================================================================== one supplier

const SupplierPanel = ({
  card, board, periodDays, can, busy, medicines, vendors, onBack, onEdit, onSave, onSend, onShare, onAddToReview, onDiscard, onAddItem,
  onSkip, onMove, skipFor, moveFor, closeSkip, closeMove, doSkip, doMove, shareDialog,
}) => {
  const [open, setOpen] = useState({});
  const [showWaiting, setShowWaiting] = useState(false);
  const [adding, setAdding] = useState(false);

  const sorted = [...card.lines].sort((a, b) => rank(a.tier) - rank(b.tier)
    || String(a.runOut || '9999').localeCompare(String(b.runOut || '9999')) || a.name.localeCompare(b.name));
  // Lines that can wait stay folded away unless somebody has ticked one.
  const main = sorted.filter(l => l.tier !== 'NEXT_ORDER' || l.include);
  const waiting = sorted.filter(l => l.tier === 'NEXT_ORDER' && !l.include);
  const ticked = card.lines.filter(l => l.include);
  const total = ticked.reduce((s, l) => s + (l.lineCost || 0), 0);
  const min = card.minimumOrderValue != null ? Number(card.minimumOrderValue) : null;
  const canChange = can('EDIT') || can('CREATE');

  const medicineOptions = (medicines || []).filter(m => !m.discontinued)
    .slice().sort((a, b) => a.name.localeCompare(b.name))
    .map(m => ({ value: m.id, label: `${m.name}${m.medicineCode ? ` · ${m.medicineCode}` : ''}` }));

  const row = (l) => {
    const t = TIER[l.tier] || TIER.FINE;
    const isOpen = !!open[l.medicineId];
    const conf = CONFIDENCE[l.confidence] || CONFIDENCE.UNKNOWN;
    const packs = l.packs === '' ? '' : Number(l.packs) || 0;
    return (
      <li key={l.medicineId} className={`border-l-4 ${t.bar} ${l.include ? 'bg-white' : 'bg-gray-50'}`}>
        <div className="flex items-start gap-3 px-4 py-3">
          <input type="checkbox" aria-label={`Order ${l.name}`} checked={!!l.include} disabled={!canChange || !card.vendorId}
            onChange={e => onEdit(l.medicineId, { include: e.target.checked })} className="w-5 h-5 mt-1 accent-emerald-600 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <p className={`font-semibold ${l.include ? 'text-gray-900' : 'text-gray-500'}`}>{l.name}</p>
            {l.vendorItemName && l.vendorItemName !== l.name && (
              <p className="text-xs text-gray-500" title="As this supplier prints it">{l.vendorItemName}</p>
            )}
            <p className="text-sm text-gray-600 mt-0.5">
              <span className={`inline-block px-2 py-0.5 rounded-full border text-xs font-medium mr-2 ${t.chip}`}>{t.short}</span>
              {l.runOut ? <>Runs out <b>{day(l.runOut)}</b></> : l.tier === 'FINE' ? 'Added by hand' : 'Run-out not known'}
              <span className="text-gray-400" title={conf.title}> · {conf.label}</span>
              {l.priceRisePercent != null && <span className="text-amber-700"> · price up {l.priceRisePercent}%</span>}
              {l.rising && <span className="text-red-700"> · use rising</span>}
            </p>
            <div className="flex items-center gap-3 mt-1 text-xs">
              <button type="button" aria-expanded={isOpen} onClick={() => setOpen(p => ({ ...p, [l.medicineId]: !isOpen }))}
                className="inline-flex items-center gap-1 text-navy-700 hover:underline">
                <Info className="w-3.5 h-3.5" />{isOpen ? 'Hide why' : 'Why?'}
              </button>
              {l.suggestedPacks != null && Number(l.packs) !== l.suggestedPacks && l.tier !== 'FINE' && (
                <span className="text-gray-500">suggested {l.suggestedPacks}</span>
              )}
              {l.moved && <span className="text-navy-700">moved here</span>}
              {l.consolidateWith && <span className="text-navy-700">{l.consolidateWith} also carries it</span>}
            </div>
            {isOpen && (
              <div className="mt-2 rounded-lg bg-gray-50 border border-gray-100 p-3 text-sm text-gray-800 leading-relaxed">
                {l.explanation}
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            <div className="flex items-center gap-1">
              <button type="button" aria-label={`One pack fewer of ${l.name}`} disabled={!canChange || packs <= 1}
                onClick={() => onEdit(l.medicineId, { packs: Math.max(1, packs - 1), include: true })}
                className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 disabled:opacity-40"><Minus className="w-4 h-4" /></button>
              <input type="number" min="1" inputMode="numeric" aria-label={`Packs of ${l.name}`} value={l.packs ?? ''} disabled={!canChange}
                onChange={e => onEdit(l.medicineId, { packs: e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10) || 0), include: true })}
                className="w-16 h-8 border border-gray-200 rounded-lg text-center font-semibold" />
              <button type="button" aria-label={`One pack more of ${l.name}`} disabled={!canChange}
                onClick={() => onEdit(l.medicineId, { packs: packs + 1, include: true })}
                className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 disabled:opacity-40"><Plus className="w-4 h-4" /></button>
            </div>
            <p className="text-xs text-gray-500">{packKind(l.supplierBottle, l.unitsPerPack)}{unitsIn(packs, l.supplierBottle, l.unitsPerPack)}</p>
            <p className="text-sm font-semibold text-gray-900">{l.lineCost != null ? rs(l.lineCost) : <span className="text-gray-400 font-normal">no price yet</span>}</p>
          </div>
          {canChange && (
            <RowActionsMenu items={[
              { key: 'move', label: 'Order from another supplier', icon: Truck, iconClass: 'text-navy-600', onClick: () => onMove(l) },
              { key: 'skip', label: "Don't order this", icon: PackageX, iconClass: 'text-red-500', onClick: () => onSkip(l) },
            ]} />
          )}
        </div>
      </li>
    );
  };

  return (
    <div className="space-y-4">
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-sm font-medium text-navy-700 hover:underline">
        <ChevronLeft className="w-4 h-4" /> All suppliers
      </button>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
        <div className="p-5 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-900">{card.vendorName}</h2>
          {card.vendorId ? (
            <div className="flex flex-wrap gap-x-6 gap-y-1 mt-1 text-sm text-gray-600">
              {!card.review && card.nextOrder && <span className="flex items-center gap-1.5 font-semibold text-navy-800"><Clock className="w-4 h-4" />{whenToOrder(card.nextOrder)}</span>}
              {!card.review && card.nextDelivery && <span>Arrives about {day(card.nextDelivery)} ({card.leadDays} day{card.leadDays === 1 ? '' : 's'}, {card.leadSource})</span>}
              {periodDays && <span className="flex items-center gap-1.5 text-emerald-800"><CalendarRange className="w-4 h-4" />Enough for {periodDays} days after it arrives</span>}
              {card.fillRate != null && <span className={card.unreliable ? 'text-amber-700' : ''}>Delivered {Math.round(card.fillRate * 100)}% of the last {card.fillOrders} order{card.fillOrders === 1 ? '' : 's'}</span>}
              {card.contactNumber && <span>{card.contactNumber}</span>}
            </div>
          ) : (
            <p className="text-sm text-gray-600 mt-1">No supplier has delivered these yet. Use "Order from another supplier" on each to choose one.</p>
          )}
          {card.draftOrderNumber && <p className="text-xs text-navy-700 mt-2">Draft {card.draftOrderNumber} is saved for this supplier.</p>}
          {card.review && (
            <div className="mt-3 rounded-xl bg-violet-50 border border-violet-200 px-4 py-3 text-sm text-violet-900 flex items-start gap-2">
              <ClipboardCheck className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
              <p>
                {card.vendorName}'s order {card.review.orderNumber} is {reviewWhere(card.review).toLowerCase()}.
                {' '}What you tick here is added to it, so {card.vendorName} gets one order
                {card.review.status === 'REVIEWED' ? '.' : `, and ${card.review.reviewerName || 'the reviewer'} checks these lines with the rest.`}
              </p>
            </div>
          )}
        </div>

        <ul className="divide-y divide-gray-100">
          {main.map(row)}
        </ul>

        {waiting.length > 0 && (
          <div className="border-t border-gray-100">
            <button type="button" onClick={() => setShowWaiting(v => !v)} aria-expanded={showWaiting}
              className="w-full flex items-center justify-between px-5 py-3 text-sm text-gray-600 hover:bg-gray-50">
              <span>{waiting.length} item{waiting.length === 1 ? '' : 's'} can wait for the next order</span>
              {showWaiting ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            {showWaiting && <ul className="divide-y divide-gray-100 border-t border-gray-100">{waiting.map(row)}</ul>}
          </div>
        )}

        {card.vendorId && canChange && (
          <div className="px-5 py-3 border-t border-gray-100">
            {adding ? (
              <div className="flex items-center gap-3 flex-wrap">
                <div className="w-96 max-w-full">
                  <Select value="" onChange={(v) => { setAdding(false); onAddItem(v); }} placeholder="Choose an item to add" options={medicineOptions} buttonClassName="p-2 rounded-lg text-sm" />
                </div>
                <button type="button" onClick={() => setAdding(false)} className="text-sm text-gray-600 hover:underline">Done</button>
              </div>
            ) : (
              <button type="button" onClick={() => setAdding(true)} className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 hover:underline">
                <Plus className="w-4 h-4" /> Add an item
              </button>
            )}
          </div>
        )}

        {card.vendorId && (
          <div className="sticky bottom-0 bg-white/95 backdrop-blur border-t border-gray-200 rounded-b-2xl px-5 py-4 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="text-sm text-gray-600">{ticked.length} item{ticked.length === 1 ? '' : 's'} ticked</p>
              <p className="text-2xl font-bold text-gray-900">{rs(total)}</p>
              {min != null && total > 0 && total < min && <p className="text-xs text-amber-700">Below this supplier's minimum order of {rs(min)}</p>}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {card.review ? (canChange && (
                <button type="button" onClick={onAddToReview} disabled={busy || ticked.length === 0}
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50">
                  <Plus className="w-4 h-4" /> Add to {card.review.orderNumber}
                </button>
              )) : (<>
              {card.draftOrderNumber && can('DELETE') && (
                <button type="button" onClick={onDiscard} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-sm text-red-700 hover:bg-red-50">
                  <Trash2 className="w-4 h-4" /> Discard draft
                </button>
              )}
              {canChange && (
                <button type="button" onClick={onSave} disabled={busy || ticked.length === 0} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50">
                  <Save className="w-4 h-4" /> Save draft
                </button>
              )}
              {can('SUBMIT_ORDER') && (
                <button type="button" onClick={onSend} disabled={busy || ticked.length === 0} title="Sends it to the supplier now, with nobody checking it"
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50">
                  <Send className="w-4 h-4" /> Send without review
                </button>
              )}
              {can('SUBMIT_ORDER') && (
                <button type="button" onClick={onShare} disabled={busy || ticked.length === 0} className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50">
                  <UserCheck className="w-4 h-4" /> Send for review
                </button>
              )}
              </>)}
            </div>
          </div>
        )}
      </div>

      {skipFor && <SkipDialog line={skipFor} onClose={closeSkip} onChoose={(reason) => doSkip(skipFor, reason)} />}
      {shareDialog}
      {moveFor && <MoveDialog line={moveFor} vendors={vendors} onClose={closeMove} onChoose={(vid) => doMove(moveFor, vid)} />}
    </div>
  );
};

const rank = (t) => ({ ORDER_TODAY: 0, THIS_ORDER: 1, FINE: 2, NEXT_ORDER: 3 }[t] ?? 4);

// =================================================================== how long to order for

/** "30 days" when every class has the same cover, else "14 to 30 days". */
const usualCover = (defaults) => {
  const covers = ['coverA', 'coverB', 'coverC'].map(k => defaults?.[k]).filter(n => n != null);
  if (covers.length === 0) return '30 days';
  const lo = Math.min(...covers);
  const hi = Math.max(...covers);
  return lo === hi ? `${lo} days` : `${lo} to ${hi} days`;
};

/**
 * The period every order on the board is planned for: the planner's own choice per item, one of
 * the usual periods, or any other typed in. The board is worked out again for the one chosen.
 */
const PeriodPicker = ({ planDays, board, loading, onChoose, notify }) => {
  const custom = planDays && !PERIODS.includes(planDays) ? planDays : null;
  const [other, setOther] = useState(custom ? String(custom) : '');
  const pending = loading && (planDays || null) !== (board.periodDays ?? null);

  const applyOther = (e) => {
    e.preventDefault();
    const n = parseInt(other, 10);
    if (!(n >= MIN_PERIOD && n <= MAX_PERIOD)) {
      notify('warning', `Choose ${MIN_PERIOD} to ${MAX_PERIOD} days.`);
      return;
    }
    onChoose(n);
  };

  const pill = (active) => `px-3 py-1.5 rounded-lg text-sm font-medium ${active ? 'bg-emerald-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`;
  const shown = board.periodDays ?? null;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
      <div className="flex items-center gap-3 flex-wrap">
        <span id="plan-period-label" className="font-semibold text-gray-800 flex items-center gap-2">
          <CalendarRange className="w-5 h-5 text-emerald-700" /> Order enough for
        </span>
        <div role="group" aria-labelledby="plan-period-label" className="flex flex-wrap gap-1 bg-gray-50 rounded-xl p-1 border border-gray-200">
          <button type="button" aria-pressed={!planDays} onClick={() => { setOther(''); onChoose(null); }} className={pill(!planDays)}>
            Planner's choice
          </button>
          {PERIODS.map(n => (
            <button key={n} type="button" aria-pressed={planDays === n} onClick={() => { setOther(''); onChoose(n); }} className={pill(planDays === n)}>
              {n} days
            </button>
          ))}
        </div>
        <form onSubmit={applyOther} className="flex items-center gap-1.5">
          <input type="number" min={MIN_PERIOD} max={MAX_PERIOD} inputMode="numeric" value={other} placeholder="Other"
            aria-label="Other number of days" onChange={e => setOther(e.target.value)}
            className={`w-20 px-2 py-1.5 border rounded-lg text-sm text-center ${custom ? 'border-emerald-500 ring-1 ring-emerald-500' : 'border-gray-200'}`} />
          <span className="text-sm text-gray-600">days</span>
          <button type="submit" disabled={!other} className="px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50">
            Plan
          </button>
        </form>
      </div>
      <p className="text-sm text-gray-600 mt-2">
        {pending
          ? `Working out what is needed for ${planDays ? `${planDays} days` : "the planner's choice"}...`
          : shown
            ? `Every item that will not last ${shown} days after its delivery is ticked, with enough to last them. Items with no sales history yet are still ordered at their alert level.`
            : `Each item is ordered to last ${usualCover(board.defaults)} after its delivery (or its own setting in Item Settings), and items that can wait for the supplier's next order are left unticked.`}
      </p>
    </div>
  );
};

// =================================================================== out for review

/** Orders a colleague is checking, or that are back from them. Their items are not proposed again. */
const ReviewCards = ({ reviews }) => {
  if (reviews.length === 0) return null;
  return (
    <section aria-label="Out for review" className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
      <p className="font-semibold text-gray-800 flex items-center gap-2"><ClipboardCheck className="w-5 h-5 text-navy-700" /> Out for review ({reviews.length})</p>
      <p className="text-xs text-gray-500 mt-0.5">Their items wait with them and are not suggested again here.</p>
      <ul className="mt-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {reviews.map((r) => {
          const back = r.status === 'REVIEWED';
          const mine = r.raisedByMe || r.reviewerIsMe;
          const body = (
            <>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 truncate">{r.vendorName}</p>
                  <p className="text-xs text-gray-500">{r.orderNumber} · {r.items} item{r.items === 1 ? '' : 's'}</p>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${back ? 'bg-amber-100 text-amber-800' : 'bg-violet-100 text-violet-800'}`}>
                  {back ? 'Back from review' : 'In review'}
                </span>
              </div>
              <p className="text-sm text-gray-700 mt-2">
                {back
                  ? `Back from ${r.reviewerName || 'the reviewer'}${r.changes ? `: ${r.changes} change${r.changes === 1 ? '' : 's'}` : ', no changes'}. ${r.raisedByMe ? 'Yours to accept.' : `With ${r.raisedByName} to accept.`}`
                  : `With ${r.reviewerIsMe ? 'you' : r.reviewerName || 'a colleague'} since ${day(String(r.sharedAt || '').slice(0, 10))}`}
              </p>
              <div className="flex items-center justify-between mt-2 text-sm">
                {r.urgent > 0 ? <span className="text-red-700">{r.urgent} will run out first</span> : <span />}
                <span className="font-semibold text-gray-900">{rs(r.total)}</span>
              </div>
            </>
          );
          return (
            <li key={r.orderId}>
              {mine ? (
                <button type="button" onClick={() => openOrderReview(r.orderId)}
                  className={`w-full text-left rounded-xl border p-3 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${back && r.raisedByMe ? 'border-amber-300 bg-amber-50/40' : 'border-gray-200'}`}>
                  {body}
                </button>
              ) : (
                <div className="rounded-xl border border-gray-200 p-3">{body}</div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
};

const SkipDialog = ({ line, onClose, onChoose }) => {
  const ref = useDialog(onClose, { confirmDiscard: false });
  return (
    <div ref={ref} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
        <div className="p-5 border-b flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Don't order {line.name}</h2>
            <p className="text-sm text-gray-600">Why? The planner remembers.</p>
          </div>
          <button type="button" aria-label="Close" onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-2">
          {SKIP_REASONS.map(r => (
            <button key={r.id} type="button" onClick={() => onChoose(r.id)}
              className="w-full text-left px-4 py-3 rounded-xl border border-gray-200 hover:border-emerald-400 hover:bg-emerald-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500">
              <p className="font-semibold text-gray-900">{r.label}</p>
              <p className="text-sm text-gray-600">{r.hint}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const MoveDialog = ({ line, vendors, onClose, onChoose }) => {
  const ref = useDialog(onClose, { confirmDiscard: false });
  const [vendorId, setVendorId] = useState('');
  const options = (vendors || [])
    .filter(v => v.status !== 'SUSPENDED' && (v.vendorType === 'PHARMACEUTICAL' || v.vendorType === 'LABORATORY') && v.id !== line.vendorId && !isOpeningStock(v))
    .slice().sort((a, b) => a.vendorName.localeCompare(b.vendorName))
    .map(v => {
      const alt = (line.alternatives || []).find(a => a.vendorId === v.id);
      return { value: v.id, label: alt ? `${v.vendorName} (last ${rs(alt.packPrice)} a pack)` : v.vendorName };
    });
  return (
    <div ref={ref} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
        <div className="p-5 border-b flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Order {line.name} from</h2>
            <p className="text-sm text-gray-600">Choose the same supplier twice and it becomes the item's usual one.</p>
          </div>
          <button type="button" aria-label="Close" onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <Select value={vendorId} onChange={setVendorId} placeholder="Choose a supplier" options={options} buttonClassName="p-2.5 rounded-lg" />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-sm font-medium">Cancel</button>
            <button type="button" disabled={!vendorId} onClick={() => onChoose(vendorId)} className="px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50">Move it</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderBoardView;
